import React, { useEffect, useState } from 'react';
import { apiClient } from '../apiClient';
import Layout from "../components/Layout/Layout";
import { useNavigate } from "react-router-dom";
import { 
  CheckCircle, 
  Clock, 
  XCircle, 
  RefreshCw, 
  Smartphone, 
  CreditCard, 
  ArrowLeft,
  Copy,
  ExternalLink,
  User,
  Mail,
  Phone,
  Calendar,
  Hash
} from 'lucide-react';
import './PaymentStatus.css';

export default function PaymentStatusPage() {
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [copied, setCopied] = useState(false);
  const [transactionType, setTransactionType] = useState('wallet'); // 'wallet' or 'ekqr'
  const [urlParams, setUrlParams] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clientTxnId = params.get('client_txn_id');
    const txnId = params.get('txn_id');
    const walletTransactionId = params.get('transactionId');
    const paymentUrlFromURL = params.get('paymentUrl');

    // Determine transaction type based on URL parameters
    if (clientTxnId && txnId) {
      // New ekqr transaction flow
      setTransactionType('ekqr');
      setUrlParams({ client_txn_id: clientTxnId, txn_id: txnId });
      setTransactionId(clientTxnId);
      fetchEkqrTransactionStatus(clientTxnId, txnId);
    } else if (walletTransactionId) {
      // Original wallet transaction flow
      setTransactionType('wallet');
      setTransactionId(walletTransactionId);
      if (paymentUrlFromURL) {
        setPaymentUrl(decodeURIComponent(paymentUrlFromURL));
      }
      fetchWalletPaymentStatus(walletTransactionId);
      
      // Poll for status updates every 5 seconds for wallet transactions
      const interval = setInterval(() => {
        fetchWalletPaymentStatus(walletTransactionId);
      }, 5000);

      return () => clearInterval(interval);
    } else {
      setError('No transaction parameters provided in URL');
      setLoading(false);
      return;
    }
  }, []);

  const fetchEkqrTransactionStatus = async (clientTxnId, txnId) => {
    try {
      const response = await apiClient.get(`/transaction/status?client_txn_id=${clientTxnId}&txn_id=${txnId}`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch transaction status');
      }

      const data = response.data;

      // Normalize/transform ekqr response (supports both old snake_case and new camelCase formats)
      const transformedData = {
        ...data,
        gatewayType: data.gatewayType || 'ekqr',
        orderId: data.orderId || data.client_txn_id,
        gatewayOrderId: data.txnId || data.id,
        upiTxnId: data.upiTxnId || data.upi_txn_id,
        amount: data.amount,
        status: data.status,
        customerInfo: {
          name: data.customerName || data.customer_name,
          email: data.customerEmail || data.customer_email,
          mobile: data.customerNumber || data.customer_mobile,
          vpa: data.customerVpa || data.customer_vpa,
        },
        paymentNote: data.paymentNote || data.p_info,
        merchant: data.merchant || data.Merchant,
        ekqrResponse: response.ekqr_response,
      };

      setStatusData(transformedData);
      setError(null);
    } catch (e) {
      console.error('EKQR Transaction Status API Error:', e);
      setError(`Failed to fetch transaction status: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletPaymentStatus = async (id) => {
    try {
      const response = await apiClient.get(`/wallet/payment-status/${id}`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch payment status');
      }

      setStatusData(response.data);
      setError(null);

      // Stop polling if payment is completed or failed
      if (response.data?.status === 'completed' || response.data?.status === 'failed') {
        // Will be cleaned up by useEffect cleanup
      }
    } catch (e) {
      console.error('Wallet Payment Status API Error:', e);
      setError(`Failed to fetch payment status: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      'completed': { 
        color: 'text-emerald-600', 
        bg: 'bg-emerald-50', 
        border: 'border-emerald-200',
        icon: CheckCircle,
        label: 'Completed',
        description: 'Payment processed successfully'
      },
      'success': { 
        color: 'text-emerald-600', 
        bg: 'bg-emerald-50', 
        border: 'border-emerald-200',
        icon: CheckCircle,
        label: 'Success',
        description: 'Payment processed successfully'
      },
      'pending': { 
        color: 'text-amber-600', 
        bg: 'bg-amber-50', 
        border: 'border-amber-200',
        icon: Clock,
        label: 'Pending',
        description: 'Awaiting payment confirmation'
      },
      'processing': { 
        color: 'text-blue-600', 
        bg: 'bg-blue-50', 
        border: 'border-blue-200',
        icon: RefreshCw,
        label: 'Processing',
        description: 'Payment is being processed'
      },
      'failed': { 
        color: 'text-red-600', 
        bg: 'bg-red-50', 
        border: 'border-red-200',
        icon: XCircle,
        label: 'Failed',
        description: 'Payment could not be processed'
      },
      'cancelled': { 
        color: 'text-red-600', 
        bg: 'bg-red-50', 
        border: 'border-red-200',
        icon: XCircle,
        label: 'Cancelled',
        description: 'Payment was cancelled'
      }
    };
    return configs[status] || configs['pending'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePayNow = () => {
    if (paymentUrl) {
      window.open(paymentUrl, '_blank');
    }
  };

  const openUPIApp = (link) => {
    window.location.href = link;
  };

  const statusConfig = statusData ? getStatusConfig(statusData.status) : null;
  const StatusIcon = statusConfig?.icon;

  if (loading) {
    return (
      <Layout>
        <div className="payment-status-container">
          <div className="payment-status-form">
            <div className="loading-spinner">
              <RefreshCw className="animate-spin" size={48} />
            </div>
            <h4 className="loading-text">Loading payment status...</h4>
            <p className="loading-description">Please wait while we fetch your transaction details.</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="payment-status-container">
          <div className="payment-status-form error-card">
            <div className="status-icon error">
              <XCircle size={64} />
            </div>
            <h4 className="status-title error">Error</h4>
            <p className="status-description">{error}</p>
            <div className="payment-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => window.location.reload()}
              >
                <RefreshCw size={16} />
                Retry
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/')}
              >
                <ArrowLeft size={16} />
                Go Home
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="payment-status-container">
        {/* Status Header */}
        <div className="payment-status-form">
          <div className={`status-header ${statusConfig?.bg} ${statusConfig?.border}`}>
            <div className={`status-icon ${statusConfig?.color}`}>
              <StatusIcon size={64} />
            </div>
            <h4 className={`status-title ${statusConfig?.color}`}>
              {statusConfig?.label}
            </h4>
            <p className="status-description">
              {statusConfig?.description}
            </p>
          </div>
        </div>

        {/* Transaction Details */}
        <div className="payment-status-details">
          <h4 className="details-title">Transaction Details</h4>
          
          {/* DESKTOP */}
          <div className="d-none d-md-none d-lg-block">
            <table className="table table-dark table-responsive">
              <thead className="custom-thead">
                <tr>
                  <th><small>Field</small></th>
                  <th><small>Value</small></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><small><Hash size={12} className="me-1" />Transaction ID</small></td>
                  <td>
                    <div className="d-flex align-items-center">
                      <small>{transactionId || statusData?.orderId || statusData?.gatewayOrderId || 'N/A'}</small>
                      {transactionId && (
                        <button 
                          className="copy-btn ms-2"
                          onClick={() => copyToClipboard(transactionId)}
                          title="Copy transaction ID"
                        >
                          <Copy size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td><small><CreditCard size={12} className="me-1" />Amount</small></td>
                  <td><small className="fw-bold">₹{statusData?.amount || 'N/A'}</small></td>
                </tr>
                {statusData?.gatewayOrderId && statusData.gatewayOrderId !== transactionId && (
                  <tr>
                    <td><small><Hash size={12} className="me-1" />Gateway Order ID</small></td>
                    <td>
                      <div className="d-flex align-items-center">
                        <small>{statusData.gatewayOrderId}</small>
                        <button 
                          className="copy-btn ms-2"
                          onClick={() => copyToClipboard(statusData.gatewayOrderId)}
                          title="Copy gateway order ID"
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {statusData?.upiTxnId && (
                  <tr>
                    <td><small><Smartphone size={12} className="me-1" />UPI Transaction ID</small></td>
                    <td>
                      <div className="d-flex align-items-center">
                        <small>{statusData.upiTxnId}</small>
                        <button 
                          className="copy-btn ms-2"
                          onClick={() => copyToClipboard(statusData.upiTxnId)}
                          title="Copy UPI transaction ID"
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {statusData?.customerInfo?.name && (
                  <tr>
                    <td><small><User size={12} className="me-1" />Customer Name</small></td>
                    <td><small>{statusData.customerInfo.name}</small></td>
                  </tr>
                )}
                {statusData?.customerInfo?.email && (
                  <tr>
                    <td><small><Mail size={12} className="me-1" />Email</small></td>
                    <td><small>{statusData.customerInfo.email}</small></td>
                  </tr>
                )}
                {statusData?.customerInfo?.mobile && (
                  <tr>
                    <td><small><Phone size={12} className="me-1" />Mobile</small></td>
                    <td><small>{statusData.customerInfo.mobile}</small></td>
                  </tr>
                )}
                {statusData?.customerInfo?.vpa && (
                  <tr>
                    <td><small><Smartphone size={12} className="me-1" />UPI ID</small></td>
                    <td><small>{statusData.customerInfo.vpa}</small></td>
                  </tr>
                )}
                {statusData?.paymentNote && (
                  <tr>
                    <td><small>Payment Note</small></td>
                    <td><small>{statusData.paymentNote}</small></td>
                  </tr>
                )}
                {statusData?.merchant && (
                  <tr>
                    <td><small>Merchant</small></td>
                    <td><small>{statusData.merchant}</small></td>
                  </tr>
                )}
                {statusData?.gatewayType && (
                  <tr>
                    <td><small>Payment Gateway</small></td>
                    <td><small className="text-uppercase">{statusData.gatewayType}</small></td>
                  </tr>
                )}
                {statusData?.createdAt && (
                  <tr>
                    <td><small><Calendar size={12} className="me-1" />Transaction Date</small></td>
                    <td><small>{formatDate(statusData.createdAt)}</small></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* MOBILE */}
          <div className="d-block d-lg-none payment-status-mobile">
            <div className="pstatus">
              <div className="items">
                <span className="fw-bold">Transaction Details</span>
                <span className="fw-bold text-success text-end">
                  {statusData?.createdAt && formatDate(statusData.createdAt)}
                </span>
              </div>
              <div className="items">
                <div className="item-name">
                  <span><Hash size={12} className="me-1" />Transaction ID</span>
                </div>
                <div className="item-details text-primary">
                  <span>{transactionId || statusData?.orderId || statusData?.gatewayOrderId || 'N/A'}</span>
                  {transactionId && (
                    <button 
                      className="copy-btn ms-2"
                      onClick={() => copyToClipboard(transactionId)}
                      title="Copy transaction ID"
                    >
                      <Copy size={12} />
                    </button>
                  )}
                </div>
              </div>
              <div className="items">
                <div className="item-name">
                  <span><CreditCard size={12} className="me-1" />Amount</span>
                </div>
                <div className="item-details">
                  <span className="fw-bold">₹{statusData?.amount || 'N/A'}</span>
                </div>
              </div>
              <div className="items">
                <div className="item-name">
                  <span>Status</span>
                </div>
                <div className="item-details">
                  <span className={`badge ${statusData?.status === 'completed' || statusData?.status === 'success' ? 'bg-success' : statusData?.status === 'pending' || statusData?.status === 'processing' ? 'bg-warning' : 'bg-danger'}`}>
                    {statusConfig?.label}
                  </span>
                </div>
              </div>
              {statusData?.gatewayOrderId && statusData.gatewayOrderId !== transactionId && (
                <div className="items">
                  <div className="item-name">
                    <span><Hash size={12} className="me-1" />Gateway Order ID</span>
                  </div>
                  <div className="item-details">
                    <span>{statusData.gatewayOrderId}</span>
                    <button 
                      className="copy-btn ms-2"
                      onClick={() => copyToClipboard(statusData.gatewayOrderId)}
                      title="Copy gateway order ID"
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                </div>
              )}
              {statusData?.upiTxnId && (
                <div className="items">
                  <div className="item-name">
                    <span><Smartphone size={12} className="me-1" />UPI Transaction ID</span>
                  </div>
                  <div className="item-details">
                    <span>{statusData.upiTxnId}</span>
                    <button 
                      className="copy-btn ms-2"
                      onClick={() => copyToClipboard(statusData.upiTxnId)}
                      title="Copy UPI transaction ID"
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                </div>
              )}
              {statusData?.customerInfo?.name && (
                <div className="items">
                  <div className="item-name">
                    <span><User size={12} className="me-1" />Customer Name</span>
                  </div>
                  <div className="item-details">
                    <span>{statusData.customerInfo.name}</span>
                  </div>
                </div>
              )}
              {statusData?.customerInfo?.email && (
                <div className="items">
                  <div className="item-name">
                    <span><Mail size={12} className="me-1" />Email</span>
                  </div>
                  <div className="item-details">
                    <span>{statusData.customerInfo.email}</span>
                  </div>
                </div>
              )}
              {statusData?.customerInfo?.mobile && (
                <div className="items">
                  <div className="item-name">
                    <span><Phone size={12} className="me-1" />Mobile</span>
                  </div>
                  <div className="item-details">
                    <span>{statusData.customerInfo.mobile}</span>
                  </div>
                </div>
              )}
              {statusData?.customerInfo?.vpa && (
                <div className="items">
                  <div className="item-name">
                    <span><Smartphone size={12} className="me-1" />UPI ID</span>
                  </div>
                  <div className="item-details">
                    <span>{statusData.customerInfo.vpa}</span>
                  </div>
                </div>
              )}
              {statusData?.paymentNote && (
                <div className="items">
                  <div className="item-name">
                    <span>Payment Note</span>
                  </div>
                  <div className="item-details">
                    <span>{statusData.paymentNote}</span>
                  </div>
                </div>
              )}
              {statusData?.merchant && (
                <div className="items">
                  <div className="item-name">
                    <span>Merchant</span>
                  </div>
                  <div className="item-details">
                    <span>{statusData.merchant}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* UPI Payment Options for pending wallet transactions */}
        {transactionType === 'wallet' && statusData?.status === 'pending' && paymentUrl && (
          <div className="payment-options-form">
            <h4>Complete Payment</h4>
            <p>
              Click the button below to complete your payment
            </p>
            
            <button 
              className="btn btn-primary btn-lg"
              onClick={handlePayNow}
            >
              <ExternalLink size={16} />
              Pay Now
            </button>
            
            <button 
              className="btn btn-secondary"
              onClick={() => setShowPaymentOptions(!showPaymentOptions)}
            >
              <Smartphone size={16} />
              UPI Payment Options
            </button>

            {showPaymentOptions && (
              <div className="upi-options">
                <h5>Open in UPI App:</h5>
                <div className="upi-apps">
                  <button 
                    className="upi-app-btn"
                    onClick={() => openUPIApp(`phonepe://pay?${new URL(paymentUrl).search.substring(1)}`)}
                  >
                    PhonePe
                  </button>
                  <button 
                    className="upi-app-btn"
                    onClick={() => openUPIApp(`paytmmp://pay?${new URL(paymentUrl).search.substring(1)}`)}
                  >
                    Paytm
                  </button>
                  <button 
                    className="upi-app-btn"
                    onClick={() => openUPIApp(`gpay://upi/pay?${new URL(paymentUrl).search.substring(1)}`)}
                  >
                    Google Pay
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="payment-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/')}
          >
            <ArrowLeft size={16} />
            Back to Home
          </button>
          
          {statusData?.status === 'completed' || statusData?.status === 'success' ? (
            <>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/user-dashboard')}
              >
                Go to Dashboard
              </button>
              {transactionType === 'wallet' && (
                <button 
                  className="btn btn-success"
                  onClick={() => navigate('/wallet')}
                >
                  View Wallet
                </button>
              )}
            </>
          ) : (
            <button 
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              <RefreshCw size={16} />
              Refresh Status
            </button>
          )}
        </div>

        {/* Copy Success Message */}
        {copied && (
          <div className="copy-success">
            <CheckCircle size={16} />
            Copied to clipboard!
          </div>
        )}
      </div>
    </Layout>
  );
}